import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Bot, Send, X, Minimize2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

import { BACKEND_URL, API } from '../config';

const renderMsg = (text) =>
  text.split('\n').map((line, i, arr) => {
    const parts = line.split(/(\*\*[^*]+\*\*)/g);
    return (
      <span key={i}>
        {parts.map((p, j) =>
          p.startsWith('**') && p.endsWith('**')
            ? <strong key={j}>{p.slice(2, -2)}</strong>
            : p
        )}
        {i < arr.length - 1 && <br />}
      </span>
    );
  });

const Atenea = ({ fullPage, defaultOpen, hideOnClose, onClosed }) => {
  const [isOpen, setIsOpen] = useState(!!fullPage || !!defaultOpen);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      loadHistory();
    }
  }, [isOpen]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadHistory = async () => {
    try {
      const response = await axios.get(`${API}/atenea/history`, { withCredentials: true });
      const history = response.data.history || [];
      setMessages(history.map(h => ({
        text: h.message,
        sender: h.sender,
        timestamp: h.timestamp
      })));

      if (history.length === 0) {
        setMessages([{
          text: '👋 ¡Hola! Soy Atenea, tu asistente inteligente de trading. ¿En qué puedo ayudarte hoy?',
          sender: 'bot',
          timestamp: new Date().toISOString()
        }]);
      }
    } catch (error) {
      console.error('Error loading history:', error);
    }
  };

  const handleSend = async () => {
    if (!input.trim()) return;

    const userMessage = { text: input, sender: 'user', timestamp: new Date().toISOString() };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      const response = await axios.post(
        `${API}/atenea/chat`,
        null,
        {
          params: { message: input },
          withCredentials: true
        }
      );

      const botMessage = {
        text: response.data.response,
        sender: 'bot',
        timestamp: response.data.timestamp
      };

      setMessages(prev => [...prev, botMessage]);
    } catch (error) {
      const errorMessage = {
        text: 'Lo siento, hubo un error. Por favor intenta de nuevo.',
        sender: 'bot',
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (!isOpen) {
    if (hideOnClose) return null;
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-16 right-6 w-10 h-10 bg-[#00D4FF] rounded-full flex items-center justify-center shadow-[0_0_20px_rgba(0,212,255,0.5)] hover:shadow-[0_0_30px_rgba(0,212,255,0.7)] transition-all z-50"
        data-testid="atenea-button"
      >
        <Bot size={18} className="text-black" />
      </button>
    );
  }

  return (
    <div className="fixed bottom-6 right-6 w-96 h-[600px] bg-[#121212] border-2 border-[#00D4FF] rounded-lg shadow-[0_0_30px_rgba(0,212,255,0.4)] flex flex-col z-50" data-testid="atenea-chat">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-[#27272A] bg-[#00D4FF]">
        <div className="flex items-center gap-3">
          <Bot size={24} className="text-black" />
          <div>
            <h3 className="font-bold text-black">Atenea</h3>
            <p className="text-xs text-black/70">Asistente IA de Trading</p>
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={() => { setIsOpen(false); if (onClosed) onClosed(); }} className="text-black hover:text-black/70">
            <X size={20} />
          </button>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.map((msg, idx) => (
          <div
            key={idx}
            className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-[80%] rounded-lg p-3 ${
                msg.sender === 'user'
                  ? 'bg-[#00D4FF] text-black'
                  : 'bg-[#1E1E1E] text-white border border-[#27272A]'
              }`}
            >
              <p className="text-sm leading-relaxed">{renderMsg(msg.text)}</p>
              <p className="text-xs mt-1 opacity-50">
                {new Date(msg.timestamp).toLocaleTimeString()}
              </p>
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex justify-start">
            <div className="bg-[#1E1E1E] text-white border border-[#27272A] rounded-lg p-3">
              <div className="flex gap-2">
                <div className="w-2 h-2 bg-[#00D4FF] rounded-full animate-bounce"></div>
                <div className="w-2 h-2 bg-[#00D4FF] rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                <div className="w-2 h-2 bg-[#00D4FF] rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></div>
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="p-4 border-t border-[#27272A]">
        <div className="flex gap-2">
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Pregúntame sobre trading..."
            className="flex-1 bg-[#1E1E1E] border-[#27272A]"
            disabled={loading}
            data-testid="atenea-input"
          />
          <Button
            onClick={handleSend}
            disabled={loading || !input.trim()}
            className="bg-[#00D4FF] text-black hover:bg-[#33DDFF]"
            data-testid="atenea-send"
          >
            <Send size={18} />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Atenea;
